import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { configsApi } from '../lib/api';
import { 
  Server, Plus, Edit, Trash2, Copy, Calendar, 
  Cpu, HardDrive, AlertCircle, Search, Filter 
} from 'lucide-react';

function DashboardPage() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const data = await configsApi.list();
      setConfigs(data.configs || []);
    } catch (err) {
      setError('Failed to load configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await configsApi.delete(id);
      setConfigs(configs.filter(c => c.id !== id));
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete configuration');
    }
  };

  const handleDuplicate = async (config) => {
    try {
      const newConfig = {
        ...config,
        name: `${config.name} (Copy)`,
      };
      delete newConfig.id;
      delete newConfig.created_at;
      delete newConfig.updated_at;
      
      const data = await configsApi.create(newConfig);
      setConfigs([data.config, ...configs]);
    } catch (err) {
      setError('Failed to duplicate configuration');
    }
  };

  const filteredConfigs = configs.filter(config =>
    config.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Server Configurations</h1>
          <p className="text-slate-400 mt-1">Manage your VM pricing configurations</p>
        </div>
        <Link
          to="/config/new"
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 
            text-white font-semibold rounded-xl hover:from-amber-600 hover:to-orange-700 transition-all"
        >
          <Plus className="w-5 h-5" />
          New Configuration
        </Link>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 mb-6 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-300">×</button>
        </div>
      )}

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search configurations..."
          className="w-full bg-slate-900/50 border border-slate-800/50 rounded-xl pl-12 pr-4 py-3 text-white 
            placeholder-slate-500 focus:border-amber-500/50 focus:ring-1 focus:ring-amber-500/20 transition-all"
        />
      </div>

      {/* Configs Grid */}
      {filteredConfigs.length === 0 ? (
        <div className="text-center py-16 bg-slate-900/30 rounded-2xl border border-slate-800/50">
          <Server className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">
            {searchTerm ? 'No configurations found' : 'No configurations yet'}
          </h3>
          <p className="text-slate-400 mb-6">
            {searchTerm 
              ? 'Try a different search term' 
              : 'Create your first server configuration to get started'}
          </p>
          {!searchTerm && (
            <Link
              to="/config/new"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-amber-500/20 text-amber-400 
                font-semibold rounded-xl border border-amber-500/30 hover:bg-amber-500/30 transition-all"
            >
              <Plus className="w-5 h-5" />
              Create Configuration
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredConfigs.map((config) => (
            <div
              key={config.id}
              className="bg-slate-900/50 border border-slate-800/50 rounded-2xl p-5 hover:border-slate-700/50 transition-all group"
            >
              {/* Card Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 
                    border border-amber-500/30 flex items-center justify-center">
                    <Server className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{config.name}</h3>
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <Calendar className="w-3 h-3" />
                      {formatDate(config.updated_at)}
                    </div>
                  </div>
                </div>
                
                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleDuplicate(config)}
                    className="p-2 text-slate-400 hover:text-amber-400 hover:bg-slate-800 rounded-lg transition-all"
                    title="Duplicate"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(config.id)}
                    className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-800 rounded-lg transition-all"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Config Summary */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="bg-slate-800/30 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-slate-400 mb-1">
                    <Cpu className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-wide">CPU</span>
                  </div>
                  <p className="text-white font-semibold">
                    {config.cpu_cores} cores
                    <span className="text-xs text-slate-500 ml-1">
                      ({config.cpu_overcommit_ratio}x)
                    </span>
                  </p>
                </div>
                <div className="bg-slate-800/30 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-slate-400 mb-1">
                    <HardDrive className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-wide">Storage</span>
                  </div>
                  <p className="text-white font-semibold">
                    {((config.storage_disk_size_gb * config.storage_disk_count) / 1024).toFixed(1)} TB
                  </p>
                </div>
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="px-2 py-1 text-xs bg-slate-800/50 text-slate-400 rounded-md">
                  {config.storage_type}
                </span>
                <span className="px-2 py-1 text-xs bg-slate-800/50 text-slate-400 rounded-md">
                  {config.amortization_months}mo amort
                </span>
                {config.is_shared && (
                  <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded-md">
                    Shared
                  </span>
                )}
              </div>

              {/* Actions */}
              <Link
                to={`/config/${config.id}`}
                className="flex items-center justify-center gap-2 w-full py-2.5 bg-slate-800/50 text-slate-300 
                  rounded-xl hover:bg-slate-800 hover:text-white transition-all font-medium"
              >
                <Edit className="w-4 h-4" />
                Edit Configuration
              </Link>

              {/* Delete Confirmation */}
              {deleteConfirm === config.id && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                  <p className="text-sm text-red-400 mb-3">Delete this configuration?</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDelete(config.id)}
                      className="flex-1 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 
                        transition-all text-sm font-medium"
                    >
                      Delete
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(null)}
                      className="flex-1 py-2 bg-slate-800/50 text-slate-300 rounded-lg hover:bg-slate-800 
                        transition-all text-sm font-medium"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default DashboardPage;
